import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import StudentForm from "@/components/StudentForm";
import StudentList from "@/pages/StudentList";
import Treino from "@/pages/Treino";
import Agenda from "@/pages/Agenda";
import AvaliacaoFisica from "@/pages/AvaliacaoFisica";
import Financeiro from "@/pages/Financeiro";
import Planos from "@/pages/Planos";
import SuspendedScreen from "@/pages/SuspendedScreen";
import AlunoView from "@/pages/AlunoView";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Dumbbell, UserPlus, Users, LogOut, ClipboardList, CalendarDays, Activity, Wallet, AlertTriangle, X, CreditCard } from "lucide-react";

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("agenda");
  const [editing, setEditing] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [defaultersCount, setDefaultersCount] = useState(0);
  const [alertDismissed, setAlertDismissed] = useState(false);
  const [finSection, setFinSection] = useState("resumo");

  useEffect(() => {
    api.get("/financial/defaulters").then(({ data }) => setDefaultersCount((data.defaulters || []).length)).catch(() => {});
  }, [refreshKey]);

  const goRegister = () => { setEditing(null); setTab("register"); };
  const onEdit = (s) => { setEditing(s); setTab("register"); };
  const onSaved = () => { setRefreshKey((k) => k + 1); setEditing(null); setTab("list"); };
  const goInadimplentes = () => { setEditing(null); setFinSection("inadimplentes"); setTab("financeiro"); };
  const onOpenStudent = async (id) => {
    try { const { data } = await api.get(`/students/${id}`); setEditing(data); setTab("register"); } catch (e) { console.error("Falha ao abrir ficha do aluno:", e); toast.error("Não foi possível abrir a ficha do aluno."); }
  };

  if (user && user.role === "aluno") return <AlunoView />;
  if (user && !user.is_master && user.access_allowed === false) return <SuspendedScreen />;

  return (
    <div className="min-h-screen bg-[#0A0D12]">
      <header className="border-b border-[#263147] bg-[#0A0D12]/90 backdrop-blur sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-emerald-500 flex items-center justify-center">
              <Dumbbell className="w-5 h-5 text-[#0A0D12]" />
            </div>
            <span className="text-lg font-bold font-heading text-slate-100">FitTrainer</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-400 hidden sm:block">{user?.email}</span>
            <Button variant="ghost" size="sm" onClick={logout} data-testid="logout-button"
              className="text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg">
              <LogOut className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">Sair</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {user?.is_demo && (
          <div data-testid="demo-banner" className="mb-4 flex items-center gap-3 bg-blue-500/10 border border-blue-500/30 rounded-2xl px-4 py-3">
            <Activity className="w-5 h-5 text-blue-400 shrink-0" />
            <p className="text-sm text-slate-200 flex-1"><span className="font-semibold text-blue-400">Modo demonstração</span> — dados fictícios, acesso temporário de 6 horas.</p>
          </div>
        )}
        {defaultersCount > 0 && !alertDismissed && (
          <div data-testid="defaulters-alert" className="mb-4 flex items-center gap-3 bg-red-500/10 border border-red-500/30 rounded-2xl px-4 py-3">
            <AlertTriangle className="w-5 h-5 text-red-400 shrink-0" />
            <p className="text-sm text-slate-200 flex-1"><span className="font-semibold text-red-400">{defaultersCount}</span> aluno(s) inadimplente(s) hoje</p>
            <Button type="button" size="sm" data-testid="defaulters-alert-view" onClick={goInadimplentes} className="h-8 bg-red-500 hover:bg-red-600 text-white rounded-lg px-3">Ver</Button>
            <button type="button" data-testid="defaulters-alert-dismiss" onClick={() => setAlertDismissed(true)} className="text-slate-500 hover:text-slate-300"><X className="w-4 h-4" /></button>
          </div>
        )}
        <div className="flex items-center gap-2 mb-6 bg-[#181F2C] border border-[#263147] rounded-2xl p-1.5 overflow-x-auto no-scrollbar sm:w-auto sm:inline-flex">
          <button data-testid="nav-tab-agenda" onClick={() => { setEditing(null); setTab("agenda"); }}
            className={`shrink-0 whitespace-nowrap flex items-center justify-center gap-2 h-11 px-5 rounded-xl text-sm font-medium transition-colors ${
              tab === "agenda" ? "bg-emerald-500 text-[#0A0D12]" : "text-slate-400 hover:text-slate-200"
            }`}>
            <CalendarDays className="w-4 h-4" /> Agenda
          </button>
          <button data-testid="nav-tab-register-student" onClick={goRegister}
            className={`shrink-0 whitespace-nowrap flex items-center justify-center gap-2 h-11 px-5 rounded-xl text-sm font-medium transition-colors ${
              tab === "register" ? "bg-emerald-500 text-[#0A0D12]" : "text-slate-400 hover:text-slate-200"
            }`}>
            <UserPlus className="w-4 h-4" /> {editing ? "Editar Aluno" : "Cadastrar Aluno"}
          </button>
          <button data-testid="nav-tab-student-list" onClick={() => { setEditing(null); setTab("list"); }}
            className={`shrink-0 whitespace-nowrap flex items-center justify-center gap-2 h-11 px-5 rounded-xl text-sm font-medium transition-colors ${
              tab === "list" ? "bg-emerald-500 text-[#0A0D12]" : "text-slate-400 hover:text-slate-200"
            }`}>
            <Users className="w-4 h-4" /> Lista de Alunos
          </button>
          <button data-testid="nav-tab-treino" onClick={() => { setEditing(null); setTab("treino"); }}
            className={`shrink-0 whitespace-nowrap flex items-center justify-center gap-2 h-11 px-5 rounded-xl text-sm font-medium transition-colors ${
              tab === "treino" ? "bg-emerald-500 text-[#0A0D12]" : "text-slate-400 hover:text-slate-200"
            }`}>
            <ClipboardList className="w-4 h-4" /> Treino
          </button>
          <button data-testid="nav-tab-avaliacao" onClick={() => { setEditing(null); setTab("avaliacao"); }}
            className={`shrink-0 whitespace-nowrap flex items-center justify-center gap-2 h-11 px-5 rounded-xl text-sm font-medium transition-colors ${
              tab === "avaliacao" ? "bg-emerald-500 text-[#0A0D12]" : "text-slate-400 hover:text-slate-200"
            }`}>
            <Activity className="w-4 h-4" /> Avaliação
          </button>
          <button data-testid="nav-tab-financeiro" onClick={() => { setEditing(null); setFinSection("resumo"); setTab("financeiro"); }}
            className={`shrink-0 whitespace-nowrap flex items-center justify-center gap-2 h-11 px-5 rounded-xl text-sm font-medium transition-colors ${
              tab === "financeiro" ? "bg-emerald-500 text-[#0A0D12]" : "text-slate-400 hover:text-slate-200"
            }`}>
            <Wallet className="w-4 h-4" /> Financeiro
          </button>
          {user?.is_master && (
            <button data-testid="nav-tab-planos" onClick={() => { setEditing(null); setTab("planos"); }}
              className={`shrink-0 whitespace-nowrap flex items-center justify-center gap-2 h-11 px-5 rounded-xl text-sm font-medium transition-colors ${
                tab === "planos" ? "bg-emerald-500 text-[#0A0D12]" : "text-slate-400 hover:text-slate-200"
              }`}>
              <CreditCard className="w-4 h-4" /> Planos
            </button>
          )}
        </div>

        <div>
          <ErrorBoundary key={tab}>
          {tab === "register" ? (
            <>
              <h1 className="text-2xl sm:text-3xl font-bold font-heading text-slate-100 mb-1">
                {editing ? "Editar Aluno" : "Cadastro de Aluno"}
              </h1>
              <p className="text-sm text-slate-400 mb-6">Preencha os dados do aluno. Campos com * são obrigatórios.</p>
              <StudentForm key={editing?.id || "new"} initial={editing} onSaved={onSaved}
                onCancel={editing ? goRegister : undefined} />
            </>
          ) : tab === "list" ? (
            <>
              <h1 className="text-2xl sm:text-3xl font-bold font-heading text-slate-100 mb-1">Lista de Alunos</h1>
              <p className="text-sm text-slate-400 mb-6">Alunos ativos são exibidos por padrão. Use o filtro para ver inativos.</p>
              <StudentList onEdit={onEdit} refreshKey={refreshKey} />
            </>
          ) : tab === "treino" ? (
            <Treino />
          ) : tab === "agenda" ? (
            <Agenda />
          ) : tab === "financeiro" ? (
            <Financeiro onOpenStudent={onOpenStudent} initialSection={finSection} />
          ) : tab === "planos" ? (
            <Planos />
          ) : (
            <AvaliacaoFisica />
          )}
          </ErrorBoundary>
        </div>
      </main>
    </div>
  );
}
